import { createRoute, lazyRouteComponent, notFound } from '@tanstack/react-router';
import { z } from 'zod';
import { isApiError } from '@/api/errors';
import { appRoute } from '@/app/layout-routes';
import { requirePermissions } from '@/app/route-guards';
import { filterParam } from '@/lib/url-state';
import { playerQuery, playersListQuery, playersSearchSchema, sessionsSearchSchema } from './api';
import { PlayerNotFound } from './pages/player-not-found';

export const playersListRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'players',
  validateSearch: playersSearchSchema,
  beforeLoad: ({ context }) => requirePermissions(context, 'VIEW_LIST_PLAYERS'),
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) => {
    void context.queryClient.prefetchQuery(playersListQuery(deps));
  },
  component: lazyRouteComponent(() => import('./pages/players-list-page'), 'PlayersListPage'),
});

export const PLAYER_TABS = ['overview', 'rounds', 'rtp', 'session'] as const;

const playerId = (raw: string | undefined) => (raw && /^\d+$/.test(raw) ? Number(raw) : null);

export const playerDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'players/$playerId',
  validateSearch: z.object({
    tab: filterParam.enum(PLAYER_TABS),
    game: filterParam.number(),
    from: filterParam.date(),
    to: filterParam.date(),
  }),
  beforeLoad: ({ context }) => requirePermissions(context, 'VIEW_SINGLE_PLAYERS'),
  loader: async ({ context, params }) => {
    const id = playerId(params.playerId);
    if (id === null) throw notFound();
    try {
      await context.queryClient.ensureQueryData(playerQuery(id));
    } catch (error) {
      // Another operator's player answers 404 too, on purpose.
      if (isApiError(error) && error.code === 'not_found') throw notFound();
      throw error;
    }
  },
  staticData: { crumbs: (params) => [{ label: `Player #${params.playerId ?? ''}` }] },
  notFoundComponent: PlayerNotFound,
  component: lazyRouteComponent(() => import('./pages/player-detail-page'), 'PlayerDetailPage'),
});

export const sessionsListRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'sessions',
  validateSearch: sessionsSearchSchema,
  beforeLoad: ({ context }) => requirePermissions(context, 'VIEW_LIST_PLAYERS'),
  component: lazyRouteComponent(() => import('./pages/sessions-page'), 'SessionsPage'),
});

export const sessionDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'sessions/$playerId',
  beforeLoad: ({ context }) => requirePermissions(context, 'VIEW_SINGLE_PLAYERS'),
  staticData: { crumbs: (params) => [{ label: `Player #${params.playerId ?? ''}` }] },
  component: lazyRouteComponent(() => import('./pages/sessions-page'), 'SessionDetailPage'),
});

export const pinsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'pins',
  beforeLoad: ({ context }) => requirePermissions(context, 'VIEW_LIST_PLAYERS'),
  component: lazyRouteComponent(() => import('./pages/pins-page'), 'PinsPage'),
});

export const playersRoutes = [
  playersListRoute,
  playerDetailRoute,
  sessionsListRoute,
  sessionDetailRoute,
  pinsRoute,
] as const;
